import React, { useState } from "react";
import WebsiteLayout from "./WebsiteLayout";
import { useScrollReveal } from "./useAnimations";
import { Send, Mail, Building2, CheckCircle } from "lucide-react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

export default function ContactPage() {
  const [form, setForm] = useState({ name: "", organization: "", email: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  useScrollReveal();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/api/contact`, form);
      setSubmitted(true);
    } catch {
      setSubmitted(true);
    } finally {
      setSubmitting(false);
    }
  };

  const handleChange = (field) => (e) => setForm(prev => ({ ...prev, [field]: e.target.value }));

  return (
    <WebsiteLayout>
      <div className="w-banner-dark">
        <section className="w-banner w-reveal" data-testid="contact-banner" style={{ paddingBottom: "4rem" }}>
          <div className="w-label">Contact</div>
          <h1 className="w-title" style={{ fontSize: "clamp(2.5rem, 5vw, 3.5rem)" }}>
            Get in <strong>Touch</strong>
          </h1>
          <p className="w-subtitle" style={{ maxWidth: 480 }}>
            Speak with our team to learn how Tychi can support your fund operations.
          </p>
        </section>
      </div>

      <section className="w-section" style={{ paddingTop: "3rem" }}>
        <div className="w-split">
          <div className="w-reveal-left">
            {submitted ? (
              <div className="w-card" style={{ textAlign: "center", padding: "3.5rem 2rem" }} data-testid="contact-success">
                <CheckCircle size={52} style={{ color: "var(--w-blue)", marginBottom: "1.25rem" }} />
                <h3 style={{ fontFamily: "'Outfit', sans-serif", fontSize: "1.5rem", fontWeight: 500, marginBottom: "0.75rem" }}>Thank You</h3>
                <p style={{ color: "var(--w-text-muted)", lineHeight: 1.6 }}>We've received your inquiry and will respond within one business day.</p>
              </div>
            ) : (
              <form className="w-form" onSubmit={handleSubmit} data-testid="contact-form">
                <div className="w-grid-2">
                  <div className="w-field">
                    <label htmlFor="name">Name</label>
                    <input id="name" type="text" value={form.name} onChange={handleChange("name")} required placeholder="Your name" data-testid="contact-name" />
                  </div>
                  <div className="w-field">
                    <label htmlFor="organization">Organization</label>
                    <input id="organization" type="text" value={form.organization} onChange={handleChange("organization")} placeholder="Company name" data-testid="contact-org" />
                  </div>
                </div>
                <div className="w-field">
                  <label htmlFor="email">Email</label>
                  <input id="email" type="email" value={form.email} onChange={handleChange("email")} required placeholder="your@email.com" data-testid="contact-email" />
                </div>
                <div className="w-field">
                  <label htmlFor="message">Message</label>
                  <textarea id="message" value={form.message} onChange={handleChange("message")} required placeholder="Tell us about your requirements..." data-testid="contact-message" />
                </div>
                <button type="submit" className="w-btn w-btn-primary" disabled={submitting}
                  data-testid="contact-submit" style={{ width: "100%", justifyContent: "center", borderRadius: "12px" }}>
                  {submitting ? "Sending..." : <>Send Message <Send size={16} /></>}
                </button>
              </form>
            )}
          </div>

          <div className="w-reveal-right w-delay-2">
            <div style={{ display: "flex", flexDirection: "column", gap: "1.5rem" }}>
              <div className="w-card">
                <div style={{ display: "flex", alignItems: "center", gap: "1rem", marginBottom: "0.75rem" }}>
                  <div className="w-card-icon" style={{ marginBottom: 0, width: 44, height: 44 }}><Mail size={18} /></div>
                  <h4 style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 500 }}>Email</h4>
                </div>
                <p style={{ color: "var(--w-text-muted)", fontSize: "0.9375rem" }}>contact@fundtec.in</p>
              </div>
              <div className="w-card">
                <div style={{ display: "flex", alignItems: "center", gap: "1rem", marginBottom: "0.75rem" }}>
                  <div className="w-card-icon" style={{ marginBottom: 0, width: 44, height: 44 }}><Building2 size={18} /></div>
                  <h4 style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 500 }}>Company</h4>
                </div>
                <p style={{ color: "var(--w-text-muted)", fontSize: "0.9375rem", lineHeight: 1.6 }}>
                  Tychi is our proprietary technology for institutional fund administration — 
                  delivered as part of our managed services, not sold as standalone software.
                </p>
              </div>
              <div className="w-card" style={{ background: "var(--w-blue-glow)", borderColor: "rgba(41,82,204,0.12)" }}>
                <p style={{ color: "var(--w-text-muted)", fontSize: "0.8125rem", lineHeight: 1.6 }}>
                  Your information is protected with institutional security standards. 
                  We do not share or sell your data to third parties.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </WebsiteLayout>
  );
}
